import React, { useEffect, useState } from "react";
import {
  Card,
  Text,
  Title,
  Button,
  TextInput,
  Table,
  TableHead,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableCell,
  Badge,
  Callout,
  Flex,
  Divider,
} from "@tremor/react";
import {
  KeyIcon,
  PlusIcon,
  TrashIcon,
  ClipboardDocumentIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  LockClosedIcon,
} from "@heroicons/react/24/outline";
import { Dialog, DialogPanel, DialogTitle } from "@headlessui/react";
import DeleteConfirm from "../../components/DeleteConfirm";
import { useAuth } from "../../services/AuthProvider";
import * as apiKeyService from "../../services/api-keys";
import { toFriendlyTime } from "../../services/utils";

export default function ApiKeys() {
  const { currentUser } = useAuth();
  const [apiKeys, setApiKeys] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [modalError, setModalError] = useState(null);
  const [newKeyName, setNewKeyName] = useState("");
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedKeyId, setSelectedKeyId] = useState(null);
  const [newKeyCreated, setNewKeyCreated] = useState(null);
  const [copied, setCopied] = useState(false);

  // Check if user has premium access with API ability
  const hasPremiumApiAccess = currentUser?.isPremium
  
  const fetchApiKeys = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const keys = await apiKeyService.getApiKeys();
      setApiKeys(keys);
    } catch (err) {
      console.error("Error fetching API keys:", err);
      setError(err.message || "Failed to load API keys");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // Only fetch if user has premium API access
    if (hasPremiumApiAccess) {
      fetchApiKeys();
    }
  }, [hasPremiumApiAccess]);

  const handleCreateKey = async () => {
    try {
      setIsLoading(true);
      setError(null);
      setModalError(null);

      if (!newKeyName.trim()) {
        setModalError("Please provide a name for your API key");
        return;
      }

      const newKey = await apiKeyService.createApiKey(newKeyName);
      setApiKeys([newKey, ...apiKeys]);
      setNewKeyCreated(newKey);
      setNewKeyName("");
      setCreateDialogOpen(false);
    } catch (err) {
      console.error("Error creating API key:", err);
      if (err.response && err.response.data && err.response.data.error) {
        setModalError(err.response.data.error);
      } else {
        setModalError(err.message || "Failed to create API key");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteKey = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      await apiKeyService.deleteApiKey(selectedKeyId);
      setApiKeys(apiKeys.filter(key => key._id !== selectedKeyId));
      setDeleteDialogOpen(false);
      setSelectedKeyId(null);
    } catch (err) {
      console.error("Error deleting API key:", err);
      setError(err.message || "Failed to delete API key");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopyToken = (token) => {
    navigator.clipboard.writeText(token);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const openDeleteDialog = (keyId) => {
    setSelectedKeyId(keyId);
    setDeleteDialogOpen(true);
  };

  // If user is not premium with API access
  if (!hasPremiumApiAccess) {
    return (
      <div className="mt-5">
      <section>
        <div className="grid grid-cols-1 gap-x-14 gap-y-8 md:grid-cols-3">
          <div>
            <h2 className="scroll-mt-10 font-semibold text-gray-900 dark:text-gray-50">
              <Flex>
                <KeyIcon className="h-5 w-5 mr-2" />
                API Keys
              </Flex>
            </h2>
            <p className="mt-1 text-sm leading-6 text-gray-500 dark:text-gray-400">
              Secure access to the Zafron API.
            </p>
          </div>
          <div className="md:col-span-2">
            <Card>
              <Callout
                title="Premium Feature"
                icon={LockClosedIcon}
                color="amber"
              >
                <p>API access is available exclusively to premium subscribers.</p>
                <Button 
                  className="mt-3"
                  size="xs"
                  onClick={() => window.location.href = "/checkout"}
                >
                  Upgrade Now
                </Button>
              </Callout>
            </Card>
          </div>
        </div>
      </section>
      </div>
    );
  }

  return (
    <div className="mt-5">
    <section>
      <div className="grid grid-cols-1 gap-x-14 gap-y-8 md:grid-cols-3">
        <div>
          <h2 className="scroll-mt-10 font-semibold text-gray-900 dark:text-gray-50">
            <Flex>

              API Keys
            </Flex>
          </h2>
          <p className="mt-1 text-sm leading-6 text-gray-500 dark:text-gray-400">
            Manage your API keys for programmatic access to Zafron.
          </p>
        </div>
        <div className="md:col-span-2">
          <Card>
            {error && (
              <Callout
                title="Error"
                icon={ExclamationTriangleIcon}
                color="rose"
                className="mb-4"
              >
                {error}
              </Callout>
            )}

            {newKeyCreated && (
              <Callout
                title="API Key Created"
                icon={CheckCircleIcon}
                color="teal"
                className="mb-4"
              >
                <p className="mb-2">
                  Your new API key has been created. Copy this key now as you won't be able to see it again!
                </p>
                <div className="flex items-center bg-gray-100 dark:bg-gray-800 p-2 rounded-md mb-2">
                  <code className="text-xs text-gray-800 dark:text-gray-100 mr-2 flex-1 overflow-x-auto whitespace-nowrap">
                    {newKeyCreated.token}
                  </code>
                  <Button
                    icon={copied ? CheckCircleIcon : ClipboardDocumentIcon}
                    variant="secondary"
                    size="xs"
                    onClick={() => handleCopyToken(newKeyCreated.token)}
                  >
                    {copied ? "Copied" : "Copy"}
                  </Button>
                </div>
                <Button
                  size="xs"
                  onClick={() => setNewKeyCreated(null)}
                >
                  I've copied it
                </Button>
              </Callout>
            )}

            <Flex justifyContent="between" className="mb-4">
              <Title>Your API Keys</Title>
              <Button
                icon={PlusIcon}
                onClick={() => setCreateDialogOpen(true)}
                disabled={isLoading}
              >
                Create New API Key
              </Button>
            </Flex>

            {apiKeys.length === 0 ? (
              <Text>You don't have any API keys yet. Create one to get started.</Text>
            ) : (
              <Table>
                <TableHead>
                  <TableRow>
                    <TableHeaderCell>Name</TableHeaderCell>
                    <TableHeaderCell>Created</TableHeaderCell>
                    <TableHeaderCell>Last Used</TableHeaderCell>
                    <TableHeaderCell>Status</TableHeaderCell>
                    <TableHeaderCell>Actions</TableHeaderCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {apiKeys.map((key) => (
                    <TableRow key={key._id}>
                      <TableCell>{key.name}</TableCell>
                      <TableCell>{toFriendlyTime(key.createdAt)}</TableCell>
                      <TableCell>
                        {key.lastUsed ? toFriendlyTime(key.lastUsed) : "Never"}
                      </TableCell>
                      <TableCell>
                        <Badge
                          color={key.enabled ? "green" : "red"}
                          size="xs"
                        >
                          {key.enabled ? "Active" : "Disabled"}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Button
                          icon={TrashIcon}
                          variant="secondary"
                          color="red"
                          size="xs"
                          onClick={() => openDeleteDialog(key._id)}
                        >
                          Delete
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}

            <Callout
              title="API Security"
              color="blue"
              className="mt-4"
            >
              <Text className="text-xs">
                API keys provide full access to your Zafron account. Keep them secure and never
                share them publicly. If you suspect a key has been compromised, delete it immediately
                and create a new one.
              </Text>
            </Callout>

            <Divider className="my-6" />
            
            <Title className="mt-6">
              <Flex alignItems="baseline">
                How to Use Your API Key
              </Flex>
            </Title>
            <div className="mt-3 rounded-md bg-gray-50 dark:bg-gray-800 p-4">
              <Text className="mb-3">
                Include your API key in the Authorization header when making requests:
              </Text>
              <code className="block bg-gray-100 dark:bg-gray-900 p-3 rounded text-xs font-mono overflow-x-auto">
                <span className="text-blue-600">curl</span> -X GET https://api.zafron.dev/devices \<br />
                {"  "}-H <span className="text-green-600">"Authorization: Bearer YOUR_API_KEY"</span>
              </code>

              <Title className="mt-4 text-sm">JavaScript Example</Title>
              <code className="block bg-gray-100 dark:bg-gray-900 p-3 rounded text-xs font-mono overflow-x-auto whitespace-pre">
{`// Fetch devices
const fetchDevices = async () => {
  const response = await fetch('https://api.zafron.dev/devices', {
    method: 'GET',
    headers: {
      'Authorization': 'Bearer YOUR_API_KEY',
      'Content-Type': 'application/json'
    }
  });
  
  return await response.json();
};

// Query device measurements
const getMeasurements = async (deviceId, channelId) => {
  const response = await fetch(\`https://api.zafron.dev/api/devices/\${deviceId}/measurements/\${channelId}\`, {
    method: 'GET',
    headers: {
      'Authorization': 'Bearer YOUR_API_KEY',
      'Content-Type': 'application/json'
    }
  });
  
  return await response.json();
};`}
              </code>
              
              <Title className="mt-4 text-sm">Python Example</Title>
              <code className="block bg-gray-100 dark:bg-gray-900 p-3 rounded text-xs font-mono overflow-x-auto whitespace-pre">
{`import requests

# Fetch devices
def get_devices():
    headers = {
        'Authorization': 'Bearer YOUR_API_KEY',
        'Content-Type': 'application/json'
    }
    
    response = requests.get('https://api.zafron.dev/devices', headers=headers)
    return response.json()

# Query device measurements
def get_measurements(device_id, channel_id):
    headers = {
        'Authorization': 'Bearer YOUR_API_KEY',
        'Content-Type': 'application/json'
    }
    
    response = requests.get(
        f'https://api.zafron.dev/api/devices/{device_id}/measurements/{channel_id}',
        headers=headers
    )
    return response.json()`}
              </code>
              
              <Text className="mt-4 text-xs text-gray-500 dark:text-gray-400">
                For more detailed API documentation, please visit our{" "}
                <a href="https://zafron.dev/docs/api" className="text-blue-600 hover:underline">
                  API documentation
                </a>.
              </Text>
            </div>
          </Card>
        </div>
      </div>

      {/* Create API Key Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        className="relative z-50"
      >
        <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <DialogPanel className="w-full max-w-md rounded bg-white dark:bg-gray-900 p-6">
            <DialogTitle className="text-lg font-medium mb-4">
              Create New API Key
            </DialogTitle>
            {modalError && (
              <Callout
                title="Error"
                icon={ExclamationTriangleIcon}
                color="rose"
                className="mb-4"
              >
                {modalError}
              </Callout>
            )}
            <div className="mb-4">
              <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                API Key Name
              </label>
              <TextInput
                placeholder="e.g., Production Server, Development Environment"
                value={newKeyName}
                onChange={(e) => setNewKeyName(e.target.value)}
                error={!!modalError}
              />
              <Text className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Give your key a descriptive name to remember where it's being used.
              </Text>
            </div>
            <div className="flex justify-end space-x-3">
              <Button
                variant="secondary"
                onClick={() => {
                  setCreateDialogOpen(false);
                  setModalError(null);
                }}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button
                onClick={handleCreateKey}
                disabled={isLoading || !newKeyName.trim()}
                loading={isLoading}
              >
                Create API Key
              </Button>
            </div>
          </DialogPanel>
        </div>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <DeleteConfirm
        isOpen={deleteDialogOpen}
        closeModal={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteKey}
        message="Are you sure you want to delete this API key? This action cannot be undone and any applications using this key will immediately lose access."
      />
    </section>
    </div>
  );
}